/**
 * /builder/<session>?catalog=<id> — load that published config into this
 * session once the socket is up, then drop the parameters so a reload does
 * not import it again.
 */

import { useRef, useEffect } from "react";
import { apiClient } from "../api/client";

export function useCatalogImport(
  connected: boolean,
  sendLoadConfig: (configData: string, merge?: boolean) => void
) {
  // Opened from the catalog: /builder/<session>?catalog=<id> loads the
  // published config into this fresh session once the socket is up.
  const catalogLoadedRef = useRef(false);
  useEffect(() => {
    if (!connected || catalogLoadedRef.current) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const catalogId = params.get("catalog");
    if (!catalogId) {
      return;
    }
    catalogLoadedRef.current = true;
    // merge=1 keeps whatever is already in the session and adds the
    // catalog assembly to it (the flow used by "Add to Scene").
    const merge = params.get("merge") === "1";
    apiClient
      .getCatalogConfig(catalogId)
      .then((configText) => {
        sendLoadConfig(configText, merge);
        // Drop the parameters so a reload keeps the session as-is instead
        // of re-importing the catalog entry on top of it.
        params.delete("catalog");
        params.delete("merge");
        const query = params.toString();
        window.history.replaceState(
          null,
          "",
          `${window.location.pathname}${query ? `?${query}` : ""}`
        );
      })
      .catch((e) => {
        console.error("Could not load the catalog entry:", e);
      });
  }, [connected, sendLoadConfig]);
}
